import { FC, useEffect, useState } from 'react';
import { Button, Space, Table, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { defaultStyles, FileIcon } from 'react-file-icon';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import DownloadFileButton from 'components/DownloadFileButton';
import FileViewerModal, { FileViewerFile } from 'components/FileViewerModal';
import styles from './styles.module.less';

interface RequestFilesTabProps {
  uuid: string;
  isActive?: boolean;
}

const RequestFilesTab: FC<RequestFilesTabProps> = ({ uuid, isActive }) => {
  const [files, setFiles] = useState<FileViewerFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    const fetchFiles = async () => {
      setLoading(true);
      try {
        const response = await apiRequests.get(`${apiRoutes.REQUESTS}/${uuid}/files`);
        setFiles(response.data.data ?? response.data);
      } catch (error) {
        asyncErrorHandler(error);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [uuid, isActive]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: FileViewerFile) => {
        const ext = record.extension?.toLowerCase() ?? '';
        const customStyles = ext in defaultStyles ? defaultStyles[ext as keyof typeof defaultStyles] : {};
        return (
          <Space size={8} style={{ alignItems: 'center' }}>
            <span className={styles.fileIcon}>
              <FileIcon extension={ext.toUpperCase()} {...customStyles} />
            </span>
            <span>{name}</span>
          </Space>
        );
      },
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size: string) =>
        size != null ? `${(Number(size) / (1024 * 1024)).toFixed(2)} MB` : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: FileViewerFile, index: number) => (
        <Space size={0}>
          <Tooltip title="View">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => { setViewerIndex(index); setViewerOpen(true); }}
            />
          </Tooltip>
          <DownloadFileButton uuid={record.uuid} name={record.name} size="small" />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey="uuid"
        dataSource={files}
        columns={columns}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ y: 'calc(100vh - 380px)' }}
      />
      <FileViewerModal
        files={files}
        initialIndex={viewerIndex}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
};

export default RequestFilesTab;
